/**
 * Parser pour la search bar unifiée des logs.
 *
 * Syntaxe supportée (V1) :
 *   - Termes libres         : `timeout` (recherché case-insensitive)
 *   - Exclusion             : `!healthcheck`
 *   - Labels                : `pod=api-abc`, `container=app`
 *   - Time preset spécial   : `last=15m` (5m, 15m, 1h, 6h, 24h, 7d…)
 *
 * Exemple :
 *   `timeout !healthcheck pod=api-abc last=15m`
 *   → {
 *       terms: ["timeout"],
 *       excludes: ["healthcheck"],
 *       labels: { pod: "api-abc" },
 *       since: "15m",
 *     }
 *
 * Aucun input n'est envoyé brut au backend : les termes atterrissent dans
 * `filter=` (regex, escapé serveur-side), les labels dans les query params
 * dédiés (`pod=`, `container=`), le `since` dans le param time.
 */

export interface ParsedSearch {
  terms: string[];
  excludes: string[];
  labels: Record<string, string>;
  since: string | null;
}

const LABEL_RE = /^([a-zA-Z_][a-zA-Z0-9_]*)=(.+)$/;

export function parseLogsSearch(input: string): ParsedSearch {
  const result: ParsedSearch = {
    terms: [],
    excludes: [],
    labels: {},
    since: null,
  };

  if (!input || !input.trim()) {
    return result;
  }

  const tokens = tokenize(input);
  for (const token of tokens) {
    if (!token) continue;

    // Exclusion : `!term`
    if (token.startsWith('!') && token.length > 1) {
      result.excludes.push(token.slice(1));
      continue;
    }

    // Label : `key=value`
    const labelMatch = token.match(LABEL_RE);
    if (labelMatch) {
      const [, key, value] = labelMatch;
      if (key === 'last' || key === 'since') {
        result.since = value;
      } else {
        result.labels[key] = value;
      }
      continue;
    }

    // Terme libre
    result.terms.push(token);
  }

  return result;
}

/**
 * Split naïf par espaces. Respecte les guillemets simples ou doubles pour
 * permettre des valeurs contenant des espaces (`level="warn error"`).
 * Suffisant pour V1 ; extension possible ensuite (échappement, etc.).
 */
function tokenize(input: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let quote: '"' | "'" | null = null;

  for (const ch of input.trim()) {
    if (quote) {
      if (ch === quote) {
        quote = null;
      } else {
        current += ch;
      }
    } else if (ch === '"' || ch === "'") {
      quote = ch as '"' | "'";
    } else if (ch === ' ') {
      if (current) {
        tokens.push(current);
        current = '';
      }
    } else {
      current += ch;
    }
  }
  if (current) tokens.push(current);
  return tokens;
}

/**
 * Compose la regex `filter=` à envoyer au backend depuis les termes/excludes
 * parsés. Format RE2 case-insensitive.
 *
 * Le backend applique lui-même `re.escape` puis `|~ "(?i)<pattern>"`, donc
 * cette fonction se contente de générer un pattern lisible.
 *
 * Multi-terms : conjonction (AND) via lookaheads.
 * Excludes : négation via lookaheads négatifs.
 *
 * Retourne null si aucun terme/exclusion → pas de filter passé.
 */
export function composeFilterRegex(parsed: ParsedSearch): string | null {
  const parts: string[] = [];
  for (const term of parsed.terms) {
    parts.push(`(?=.*${escapeRE2(term)})`);
  }
  for (const excl of parsed.excludes) {
    parts.push(`(?!.*${escapeRE2(excl)})`);
  }
  if (parts.length === 0) return null;
  return `^${parts.join('')}.*$`;
}

/** Échappe les métacharactères RE2 pour un usage littéral. */
function escapeRE2(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Applique un filtre level (INFO/WARN/ERROR) sur les termes déjà parsés.
 * Retourne un pattern regex à AND-er avec le filter existant, ou null si
 * aucun level sélectionné (= tous acceptés).
 */
export function levelFilterRegex(levels: string[]): string | null {
  if (levels.length === 0) return null;
  // Heuristique : cherche le mot du level dans la ligne, sans word boundary
  // strict (les logs varient : "level=info", "[INFO]", " INFO: ", etc.).
  const alt = levels.map(l => escapeRE2(l.toLowerCase())).join('|');
  return `(?=.*(${alt}))`;
}

/**
 * Combine parsed search + level filter en un unique pattern regex pour l'API.
 * Retourne null si rien à filtrer.
 */
export function buildFilterParam(parsed: ParsedSearch, levels: string[]): string | null {
  const partsFromParsed: string[] = [];
  for (const term of parsed.terms) {
    partsFromParsed.push(`(?=.*${escapeRE2(term)})`);
  }
  for (const excl of parsed.excludes) {
    partsFromParsed.push(`(?!.*${escapeRE2(excl)})`);
  }
  const levelPart = levelFilterRegex(levels);
  if (levelPart) partsFromParsed.push(levelPart);
  if (partsFromParsed.length === 0) return null;
  return `^${partsFromParsed.join('')}.*$`;
}
